// ref https://github.com/outline/outline/blob/bbee4b57916d79fba0dbc25a57036d3544c48a45/shared/editor/extensions/Prism.ts
import { flattenDeep } from 'lodash';
import { Node } from 'prosemirror-model';
import { Plugin, PluginKey, Transaction } from 'prosemirror-state';
import { findBlockNodes, findChildren } from 'prosemirror-utils';
import { Decoration, DecorationSet } from 'prosemirror-view';
import type { RefractorRoot } from 'refractor/lib/core.js';

import { refractor } from './refractor';

type ParsedNode = {
  text: string;
  classes: string[];
};

const cache: Record<number, { node: Node; decorations: Decoration[] }> = {};
export const PRISM_PLUGIN_KEY = new PluginKey('prism');

function parseNodes(
  node: RefractorRoot['children'],
  classNames: string[] = [],
): {
  text: string;
  classes: string[];
}[] {
  return flattenDeep(
    node.map((node) => {
      if (node.type === 'element') {
        const classes = [...classNames, ...((node.properties?.className as string[]) || [])];
        return parseNodes(node.children, classes);
      }

      return {
        text: node.value,
        classes: classNames,
      };
    }),
  );
}

function getDecorations({
  doc,
  name,
}: {
  /** The prosemirror document to operate on. */
  doc: Node;
  /** The node name. */
  name: string;
}) {
  const decorations: Decoration[] = [];
  const blocks: { node: Node; pos: number }[] = findBlockNodes(doc).filter(
    (item) => item.node.type.name === name,
  );

  blocks.forEach((block) => {
    let startPos = block.pos + 1;
    const language = block.node.attrs.language;
    if (!language || language === 'none' || !refractor.registered(language)) {
      return;
    }

    if (!cache[block.pos] || !cache[block.pos].node.eq(block.node)) {
      const nodes = refractor.highlight(block.node.textContent, language);

      const newDecorations = parseNodes(nodes.children)
        .map((node: ParsedNode) => {
          const from = startPos;
          const to = from + node.text.length;

          startPos = to;

          return {
            ...node,
            from,
            to,
          };
        })
        .filter((node) => node.classes && node.classes.length)
        .map((node) =>
          Decoration.inline(node.from, node.to, {
            class: node.classes.join(' '),
          }),
        );

      cache[block.pos] = {
        node: block.node,
        decorations: newDecorations,
      };
    }

    cache[block.pos].decorations.forEach((decoration) => {
      decorations.push(decoration);
    });
  });

  Object.keys(cache)
    .filter((pos) => !blocks.find((block) => block.pos === Number(pos)))
    .forEach((pos) => {
      delete cache[Number(pos)];
    });

  return DecorationSet.create(doc, decorations);
}

export default function prism({
  name,
}: {
  /** The node name. */
  name: string;
}) {
  let highlighted = false;

  return new Plugin({
    key: PRISM_PLUGIN_KEY,
    state: {
      init: (_, { doc }) => getDecorations({ doc, name }),
      apply: (transaction: Transaction, decorationSet, oldState, newState) => {
        const nodeName = newState.selection.$head.parent.type.name;
        const previousNodeName = oldState.selection.$head.parent.type.name;
        const codeBlockChanged =
          transaction.docChanged && [nodeName, previousNodeName].includes(name);
        const ySyncEdit = !!transaction.getMeta('y-sync$');

        if (!highlighted || codeBlockChanged || ySyncEdit) {
          highlighted = true;
          return getDecorations({ doc: transaction.doc, name });
        }

        const oldNodes = findChildren(oldState.doc, (node) => node.type.name === name);
        const newNodes = findChildren(newState.doc, (node) => node.type.name === name);

        if (oldNodes.length !== newNodes.length) {
          highlighted = true;
          return getDecorations({ doc: transaction.doc, name });
        }

        return decorationSet.map(transaction.mapping, transaction.doc);
      },
    },
    view: (view) => {
      if (!highlighted) {
        // we don't highlight code blocks on the first render as part of mounting
        // as it's expensive (relative to the rest of the document). Instead let
        // it render un-highlighted and then trigger a defered render of Prism
        // by updating the plugins metadata
        setTimeout(() => {
          view.dispatch(view.state.tr.setMeta('prism', { loaded: true }));
        }, 10);
      }

      return {};
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
    },
  });
}
